import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, PollResponseFilters, User } from '../../../types';
import React, { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';

import Papa from 'papaparse';
import { db } from '../../../firebase';
import useAuth from '../../../hooks/useAuth';

interface PollResponsePrecinctReportProps {
  filters: PollResponseFilters
}

// Helper function to convert response value to a sortable string
const getStringValue = (value: string | number | string[] | null | undefined): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

// Helper function to convert Timestamp to readable string
const formatTimestamp = (timestamp: any): string => {
  if (!timestamp) return '';
  try {
    return timestamp.toDate ? timestamp.toDate().toLocaleString() : String(timestamp);
  } catch {
    return String(timestamp);
  }
};

const PollResponsePrecinctReport: React.FC<PollResponsePrecinctReportProps> = ({ filters }) => {
  const { user } = useAuth();
  
  const [pollResponses, setPollResponses] = useState<(PollResponse & { precinct?: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVotersAndPolls = async (user: User) => {
      try {
        setLoading(true);
        
        // Base collection path for barangay
        const barangayCollectionPath = `tabulationsV2/${user.dashboard}/regions/${filters.region}/provinces/${filters.province}/districts/${filters.district}/cities/${filters.municipality}/barangays/${filters.barangay}/precincts`;
        
        // Fetch precincts in the barangay
        const precinctRef = collection(db, barangayCollectionPath);
        const precinctSnapshot = await getDocs(precinctRef);

        const allPollResponses: (PollResponse & { precinct?: string })[] = [];

        // Iterate through precincts (or just the specified precinct)
        for (const precinctDoc of precinctSnapshot.docs) {
          // Skip if a specific precinct is selected and this isn't it
          if (filters.precinct && precinctDoc.id !== filters.precinct) continue;

          const votersRef = collection(precinctDoc.ref, "voters");

          if (filters.poller) {
            const votersSnapshot = await getDocs(votersRef);
            const votersData = await Promise.all(
              votersSnapshot.docs.map(async (latestPollDoc) => {
                const voterPollResponsesRef = collection(latestPollDoc.ref, "pollResponses");
                const opStr = filters.pollerMatchType === "match" ? "==" : "!=";
                const q = query(voterPollResponsesRef, where("submittedBy", opStr, filters.poller), orderBy("submittedAt", "desc"));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                  const latestPoll = querySnapshot.docs[0].data() as PollResponse;
                  return { ...latestPoll, precinct: precinctDoc.id };  
                }
              })
            );
            
            allPollResponses.push(...votersData.filter((voter) => voter !== undefined) as (PollResponse & { precinct?: string })[]);
          } else {
            const votersSnapshot = await getDocs(votersRef);
            const votersData: (PollResponse & { precinct?: string })[] = await Promise.all(
              votersSnapshot.docs.map(async (latestPollDoc) => {
                const latestPoll = latestPollDoc.data() as PollResponse;
                return { ...latestPoll, precinct: precinctDoc.id };
              })
            );
            
            allPollResponses.push(...votersData);
          }
        }
        
        // Sort poll responses by voter name
        const sortedPollResponses = allPollResponses.sort((a, b) => 
          getStringValue(a.responses['VOTER']).localeCompare(getStringValue(b.responses['VOTER']))
        );
        
        setPollResponses(sortedPollResponses);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching voters and polls:', err);
        setError('Failed to fetch voters and polls');
        setLoading(false);
      }
    };

    if (user && filters.region && filters.province &&
      filters.district && filters.municipality && filters.barangay
    ) {
      fetchVotersAndPolls(user);
    }
  }, [user, filters]);

  // New function to export poll responses to CSV
  const exportToCSV = () => {
    // Prepare CSV data
    const csvData = pollResponses.map((pollResponse, index) => ({
      '#': index + 1,
      'Voter Name': getStringValue(pollResponse.responses['VOTER']),
      'Precinct': pollResponse.precinct,
      'Selected Candidate': getStringValue(pollResponse.responses[filters.position]),
      'Polled By': pollResponse.submittedBy,
      'Submitted At': formatTimestamp(pollResponse.submittedAt)
    }));

    // Generate CSV file
    const csv = Papa.unparse(csvData);
    
    // Create and download CSV file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `poll_responses_${filters.barangay}${filters.precinct ? '_precinct_' + filters.precinct : ''}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h6" gutterBottom>
              Poll Responses for {filters.position} in {filters.barangay}
              {filters.precinct ? ` - Precinct ${filters.precinct}` : ' (All Precincts)'}
            </Typography>
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={exportToCSV}
              disabled={pollResponses.length === 0}
            >
              Export to CSV
            </Button>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Voter Name</TableCell>
                <TableCell>Precinct</TableCell>
                <TableCell>Selected Candidate</TableCell>
                <TableCell>Polled By</TableCell>
                <TableCell>Submitted At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pollResponses.map((pollResponse, index) => (
                <TableRow key={pollResponse.id}>
                  <TableCell>{++index}</TableCell>
                  <TableCell>{getStringValue(pollResponse.responses['VOTER'])}</TableCell>
                  <TableCell>{pollResponse.precinct}</TableCell>
                  <TableCell>{getStringValue(pollResponse.responses[filters.position])}</TableCell>
                  <TableCell>{pollResponse.submittedBy}</TableCell>
                  <TableCell>{formatTimestamp(pollResponse.submittedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pollResponses.length === 0 && (
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            No voters found in this barangay
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default PollResponsePrecinctReport;
